import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';
import { ItRequisitionTypeCodes } from '@Enum/api/ItRequisitionTypeCodes.enum';
import { TypeDict } from '@Types/index';
import { LOST_SIM_CARD_ERROR, SIM_ADDON_REQUEST_ERROR } from './constants';

export const getEmailId = (
  emailIdLeft: string,
  emailIdRight: string
): string => {
  if (!emailIdLeft || !emailIdRight) {
    return '';
  }

  const output = `${emailIdLeft}.${emailIdRight}`;
  return output;
};

export const getEmailSignature = (
  emailSignatureLeft: string,
  emailSignatureRight: string
): string => {
  if (!emailSignatureLeft || !emailSignatureRight) {
    return '';
  }

  const output = `${emailSignatureLeft} ${emailSignatureRight}`;
  return output;
};

export const getPersonalMobileValue = (
  itReqType: number | null,
  intCallsNumber: string,
  contactNo: string
): string => {
  if (itReqType === ItRequisitionTypeCodes.INTERNATIONAL_CALLS) {
    return intCallsNumber;
  }

  return contactNo;
};

export const getUsernameValue = (
  itReqType: number | null,
  emailIdLeft: string | null,
  emailIdRight: string | null,
  empName: string
): string => {
  if (itReqType === ItRequisitionTypeCodes.EMAIL) {
    const output = getEmailId(emailIdLeft!, emailIdRight!);
    return output;
  }

  return empName;
};

export const getAccessoriesValue = (
  itReqType: number | null,
  emailSignatureLeft: string | null,
  emailSignatureRight: string | null,
  accessories: string[],
  accessoriesDict: TypeDict[]
): string => {
  if (itReqType === ItRequisitionTypeCodes.EMAIL) {
    const output = getEmailSignature(emailSignatureLeft!, emailSignatureRight!);
    return output;
  }

  if (itReqType === ItRequisitionTypeCodes.ACCESSORIES) {
    const accessoryCodes = accessories
      .map(
        (accessoryItem) =>
          accessoriesDict.find(
            (accessoryDictItem) =>
              accessoryDictItem.codeNum === Number(accessoryItem)
          )?.codeNum
      )
      .filter((item) => Number.isFinite(item));

    const output = accessoryCodes.join(', ');
    return output;
  }

  return '';
};

export const parseEmailId = (
  emailId: string | null
): { emailIdLeft: string; emailIdRight: string } => {
  if (!emailId) {
    return {
      emailIdLeft: '',
      emailIdRight: '',
    };
  }

  const [emailIdLeft, emailIdRight] = emailId.split('.');

  return {
    emailIdLeft,
    emailIdRight,
  };
};

export const parseEmailSignature = (
  emailSignature: string
): { emailSignatureLeft: string; emailSignatureRight: string } => {
  if (!emailSignature) {
    return {
      emailSignatureLeft: '',
      emailSignatureRight: '',
    };
  }

  const [emailSignatureLeft, emailSignatureRight] = emailSignature.split(' ');

  return {
    emailSignatureLeft,
    emailSignatureRight,
  };
};

type ParsedFields = {
  intCallsNumber: string;
  contactNo: string;
  emailIdLeft: string | null;
  emailIdRight: string | null;
  emailSignatureLeft: string | null;
  emailSignatureRight: string | null;
  accessories: string[];
};

type CountryCodeIntNumber = {
  codeCountry: string;
  intCallNumber: string;
};

export const parseIntCalls = (intCallNumber: string): CountryCodeIntNumber => {
  if (!intCallNumber.length) {
    return { codeCountry: '', intCallNumber: '' };
  }

  const splitted = intCallNumber.split(' ');
  return {
    codeCountry: splitted[0].slice(1, splitted[0].length),
    intCallNumber: splitted[1],
  };
};

export const parseSpecificFields = (
  reqItemId: number,
  personalMobile: string,
  username: string | null,
  accessories: string
): ParsedFields => {
  const output: ParsedFields = {
    intCallsNumber: EMPTY_FIELD_PLACEHOLDER,
    contactNo: EMPTY_FIELD_PLACEHOLDER,
    emailIdLeft: null,
    emailIdRight: null,
    emailSignatureLeft: null,
    emailSignatureRight: null,
    accessories: [],
  };

  if (reqItemId === ItRequisitionTypeCodes.INTERNATIONAL_CALLS) {
    output.intCallsNumber = personalMobile;
  } else {
    output.contactNo = personalMobile;
  }

  if (reqItemId === ItRequisitionTypeCodes.EMAIL) {
    const { emailIdLeft, emailIdRight } = parseEmailId(username);
    const { emailSignatureLeft, emailSignatureRight } =
      parseEmailSignature(accessories);

    output.emailIdLeft = emailIdLeft;
    output.emailIdRight = emailIdRight;
    output.emailSignatureLeft = emailSignatureLeft;
    output.emailSignatureRight = emailSignatureRight;
  }

  if (reqItemId === ItRequisitionTypeCodes.ACCESSORIES) {
    output.accessories = accessories.split(', ');
  }

  return output;
};

export const getValidationCugError = (type: ItRequisitionTypeCodes): string => {
  if (type === ItRequisitionTypeCodes.LOST_SIM_CARD) {
    return LOST_SIM_CARD_ERROR;
  }

  if (type === ItRequisitionTypeCodes.SIM_ADDON_REQUEST) {
    return SIM_ADDON_REQUEST_ERROR;
  }

  return 'Something went wrong';
};
